/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AnyAction } from 'redux';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import {
    DEFAULT_PAST_GUIDE_MAX_PAGE_LOAD,
    DEFAULT_RETRY_OPTIONS,
    FUTURE_GUIDE_MAX_PAGE_LOAD,
    GUIDE_FOCUS_HORIZONTAL_PAGE_ID,
    GUIDE_FOCUS_VERTICAL_PAGE_ID,
    GUIDE_HORIZONTAL_PAGE_LIMIT,
    GUIDE_VERTICAL_PAGE_LIMIT,
    IDLE_GRID_GUIDE_DURATION,
    IDLE_GRID_GUIDE_DURATION_IN_SECOND,
    MILLISECONDS_IN_A_SECOND,
} from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { Channel } from '../../core/entities/Channel';
import { GridGuideRow } from '../../core/entities/GridGuideRow';
import { NFREvents, SpinnerDelays } from '../../core/enums';
import {
    GuideChannelsError,
    GuideError,
    GuideRowsError,
    TypedChannelsNotFoundError,
} from '../../core/error';
import { ServicesClientImpl } from '../../core/interactors/ServicesClientImpl';
import { Locale, LocaleKeys } from '../../core/locale';
import { OperationsLogger, VuLogger } from '../../core/logging';
import { NFRLoggings } from '../../core/logging/NFRLoggings';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../core/network/NetworkManager';
import { RecoveryMechanism } from '../../core/recoverymechanism/RecoveryMechanism';
import { RetryMechanism } from '../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../core/retrymechanism/RetryOptions';
import { Storage } from '../../core/storage/Storage';
import { TChannels } from '../../core/types/TChannels';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import { WhisperTypes } from '../../views/Whisper/WhisperTypes';
import { clearActiveInteractiveComponent } from '../interactivelayer/interactiveLayer.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import {
    hideTemporaryGuideIssueOverlay,
    showTemporaryGuideIssueOverlay,
} from '../overlay/temporaryguideissueoverlay/temporaryguideissueoverlay.reducers';
import { setScreenTaskController, showEmptyGuideScreen } from '../screens/screens.reducers';
import { hideSpinner, showSpinner } from '../spinner/spinner.reducers';
import { hideWhisper, showWhisper } from '../whisper/whisper.reducer';
import {
    guideSetErrorAction,
    guideSetIsCaretVisibleAction,
    guideSetIsSpinnerActiveAction,
    loadGridGuideChannelsAction,
    loadGridGuideFutureRowsAction,
    loadGridGuidePastRowsAction,
    loadGridGuideRowsAction,
    onLeftSetPreviousPageAction,
    onRightSetNextPageAction,
    refreshGridGuideAction,
    resetGridGuideChannelsAction,
    resetGridGuideRowsAction,
    setGridGuideCacheAction,
    setGridGuideChannelsCacheAction,
    setGridGuideOffsetAction,
    setGridGuideRowsAction,
    setGridGuideRowsCacheAction,
    setGridGuideTimestampWindowAction,
    setGuideStartTimeAction,
    setIsChannelsServiceFailAction,
    setIsGuideRowsServiceFailAction,
    setIsPastGuideModeAction,
} from './guide.actions';
import {
    GUIDE_SET_ERROR,
    GUIDE_SET_IS_CARET_VISIBLE,
    GUIDE_SET_IS_SPINNER_ACTIVE,
    LOAD_GRID_GUIDE_CHANNELS,
    LOAD_GRID_GUIDE_FUTURE_ROWS,
    LOAD_GRID_GUIDE_PAST_ROWS,
    LOAD_GRID_GUIDE_ROWS,
    ON_LEFT_SET_PREVIOUS_PAGE,
    ON_RIGHT_SET_NEXT_PAGE,
    REFRESH_GRID_GUIDE,
    RESET_GRID_GUIDE_CHANNELS,
    RESET_GRID_GUIDE_ROWS,
    SET_CHANNELS_COUNT,
    SET_GRID_GUIDE_CACHE,
    SET_GRID_GUIDE_CHANNELS_CACHE,
    SET_GRID_GUIDE_OFFER_PREVIEW_DATA,
    SET_GRID_GUIDE_OFFSET,
    SET_GRID_GUIDE_ROWS,
    SET_GRID_GUIDE_ROWS_CACHE,
    SET_GRID_GUIDE_START_TIME,
    SET_GRID_GUIDE_STATION_IDS,
    SET_GRID_GUIDE_TIMESTAMP_WINDOW,
    SET_GUIDE_ROWS_SERVICE_FAIL,
    SET_IS_CHANNELS_SERVICE_FAIL,
    SET_IS_PAST_GUIDE_MODE,
} from './guide.types';
import { setHorizontalPageCountAction } from './guidehighlight/guidehighlight.actions';
import {
    setGuideHighlightToInitialPosition,
    setHorizontalPageCount,
    setHorizontalPosition,
    setVerticalPageCount,
    setVerticalPosition,
} from './guidehighlight/guidehighlight.reducers';

const TAG = 'gridGuideReducer';

const initialState: {
    startTime: Date;
    channels: Array<Channel>;
    channelsCache: Array<Channel[]>;
    stationIds: string[];
    pastGuideRows: GridGuideRow[];
    guideRows: GridGuideRow[];
    futureGuideRows: GridGuideRow[];
    guideHorizontalPageLimit: number;
    guideVerticalPageLimit: number;
    guideFocusHorizontalPageId: number;
    guideFocusVerticalPageId: number;
    guideRowsCache: Array<GridGuideRow[][]>;
    previousTimestamp: Date;
    currentTimestamp: Date;
    nextTimestamp: Date;
    offset: number;
    LIMIT: number;
    isPastGuideMode: boolean;
    channelsCount: number;
    isChannelsServiceFail: boolean;
    isGuideRowsServiceFail: boolean;
    offerId: string;
    offerPreview: object;
    isCaretVisible: boolean;
    error: GuideError | null;
    isSpinnerActive: boolean;
} = {
    startTime: new Date(),
    channels: [],
    channelsCache: Array(GUIDE_VERTICAL_PAGE_LIMIT).fill([]),
    stationIds: [],
    pastGuideRows: [],
    guideRows: [],
    futureGuideRows: [],
    guideHorizontalPageLimit: GUIDE_HORIZONTAL_PAGE_LIMIT,
    guideVerticalPageLimit: GUIDE_VERTICAL_PAGE_LIMIT,
    guideFocusHorizontalPageId: GUIDE_FOCUS_HORIZONTAL_PAGE_ID,
    guideFocusVerticalPageId: GUIDE_FOCUS_VERTICAL_PAGE_ID,
    guideRowsCache: Array(GUIDE_VERTICAL_PAGE_LIMIT).fill(
        Array(GUIDE_HORIZONTAL_PAGE_LIMIT).fill([]),
    ),
    previousTimestamp: new Date(),
    currentTimestamp: AppUtils.roundTimeToNearestInterval(new Date(), 30, true),
    nextTimestamp: new Date(),
    offset: 0,
    LIMIT: 8,
    isPastGuideMode: false,
    channelsCount: 0,
    isChannelsServiceFail: false,
    isGuideRowsServiceFail: false,
    offerId: '',
    offerPreview: {},
    isCaretVisible: false,
    error: null,
    isSpinnerActive: false,
};

const gridGuideReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_GRID_GUIDE_START_TIME: {
            return {
                ...state,
                startTime: payload.startTime,
                previousTimestamp: payload.previousTimestamp,
                currentTimestamp: payload.currentTimestamp,
                nextTimestamp: payload.nextTimestamp,
            };
        }
        case SET_GRID_GUIDE_OFFSET:
            return {
                ...state,
                offset: payload.offset,
            };
        case SET_GRID_GUIDE_STATION_IDS:
            return {
                ...state,
                stationIds: payload.stationIds,
            };
        case SET_GRID_GUIDE_TIMESTAMP_WINDOW:
            return {
                ...state,
                previousTimestamp: payload.previousTimestamp,
                currentTimestamp: payload.currentTimestamp,
                nextTimestamp: payload.nextTimestamp,
            };
        case RESET_GRID_GUIDE_ROWS:
        case SET_GRID_GUIDE_ROWS:
            return {
                ...state,
                pastGuideRows: payload.pastGuideRows,
                guideRows: payload.guideRows,
                futureGuideRows: payload.futureGuideRows,
            };
        case LOAD_GRID_GUIDE_CHANNELS:
            return {
                ...state,
                channels: payload.channels,
                channelsCount: payload.channelsCount,
                stationIds: payload.stationIds,
                isChannelsServiceFail: payload.isChannelsServiceFail,
            };
        case RESET_GRID_GUIDE_CHANNELS:
            return {
                ...state,
                channels: payload.channels,
            };
        case LOAD_GRID_GUIDE_PAST_ROWS:
            return {
                ...state,
                pastGuideRows: payload.pastGuideRows,
            };
        case LOAD_GRID_GUIDE_ROWS:
            return {
                ...state,
                guideRows: payload.guideRows,
                isGuideRowsServiceFail: payload.isGuideRowsServiceFail,
            };
        case LOAD_GRID_GUIDE_FUTURE_ROWS:
            return {
                ...state,
                futureGuideRows: payload.futureGuideRows,
            };
        case SET_IS_PAST_GUIDE_MODE:
            return {
                ...state,
                isPastGuideMode: payload.isPastGuideMode,
            };
        case ON_LEFT_SET_PREVIOUS_PAGE:
        case ON_RIGHT_SET_NEXT_PAGE:
            return { ...state, ...payload };
        case SET_CHANNELS_COUNT:
            return {
                ...state,
                channelsCount: payload.channelsCount,
            };
        case SET_IS_CHANNELS_SERVICE_FAIL:
            return {
                ...state,
                isChannelsServiceFail: payload.isChannelsServiceFail,
            };
        case GUIDE_SET_ERROR:
            return {
                ...state,
                error: payload.error,
            };
        case SET_GUIDE_ROWS_SERVICE_FAIL:
            return {
                ...state,
                isGuideRowsServiceFail: payload.isGuideRowsServiceFail,
                previousTimestamp: payload.previousTimestamp,
                currentTimestamp: payload.currentTimestamp,
                nextTimestamp: payload.nextTimestamp,
                guideRows: payload.guideRows,
                pastGuideRows: payload.pastGuideRows,
                futureGuideRows: payload.futureGuideRows,
            };
        case REFRESH_GRID_GUIDE:
            return {
                ...state,
                startTime: payload.startTime,
                currentTimestamp: payload.currentTimestamp,
                nextTimestamp: payload.nextTimestamp,
                previousTimestamp: payload.previousTimestamp,
                isPastGuideMode: payload.isPastGuideMode,
                guideRows: payload.guideRows,
                pastGuideRows: payload.pastGuideRows,
                futureGuideRows: payload.futureGuideRows,
            };
        case SET_GRID_GUIDE_ROWS_CACHE:
            return {
                ...state,
                guideRowsCache: payload.guideRowsCache,
            };
        case SET_GRID_GUIDE_CHANNELS_CACHE:
            return {
                ...state,
                channelsCache: payload.channelsCache,
            };
        case SET_GRID_GUIDE_CACHE:
            return {
                ...state,
                channelsCache: payload.channelsCache,
                guideRowsCache: payload.guideRowsCache,
            };
        case SET_GRID_GUIDE_OFFER_PREVIEW_DATA:
            return {
                ...state,
                offerPreview: payload.offerPreview,
                offerId: payload.offerId,
            };
        case GUIDE_SET_IS_CARET_VISIBLE:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case GUIDE_SET_IS_SPINNER_ACTIVE:
            return {
                ...state,
                isSpinnerActive: payload.isSpinnerActive,
            };
        default:
            return state;
    }
};

export const cacheGridGuideChannels = () => (dispatch: Function, getState: Function) => {
    const { guideVerticalPageLimit, guideFocusVerticalPageId } = getState().gridGuide;

    // Loading all top and bottom channels data
    for (let pageId = 0; pageId < guideVerticalPageLimit; pageId += 1) {
        if (pageId !== guideFocusVerticalPageId) dispatch(fetchAndUpdateChannelsCache(pageId));
    }
};

export const loadGridGuidePreviousChannels = () => (dispatch: Function, getState: Function) => {
    NFRLoggings.log(NFREvents.START_LOADING_GUIDE);
    const { channelsCache, guideRowsCache, guideVerticalPageLimit } = getState().gridGuide;

    // Load Channels with previous channelsCache and load corresponding guideRowsCache
    dispatch(onChannelsChangeUpdateGridGuide(true));

    // Shift Down and fill top most channelsCache and corresponding guideRowsCache
    const topMostPageId = 0;
    for (let i = guideVerticalPageLimit - 1; i > topMostPageId; i -= 1) {
        channelsCache[i] = channelsCache[i - 1];
        guideRowsCache[i] = guideRowsCache[i - 1];
    }
    channelsCache[topMostPageId] = [];
    guideRowsCache[topMostPageId] = [];

    dispatch(setGridGuideCacheAction(channelsCache, guideRowsCache));
    // dispatch(fetchAndUpdateChannelsCache(topMostPageId));
    dispatch(clearGridGuideCache());
};

export const loadGridGuideNextChannels = () => (dispatch: Function, getState: Function) => {
    NFRLoggings.log(NFREvents.START_LOADING_GUIDE);
    const { channelsCache, guideRowsCache, guideVerticalPageLimit } = getState().gridGuide;

    // Load Channels with next channelsCache and load corresponding guideRowsCache
    dispatch(onChannelsChangeUpdateGridGuide());

    // Shift Up and fill bottom most channelsCache and corresponding guideRowsCache
    const bottomMostPageId: number = guideVerticalPageLimit - 1;
    for (let i = 0; i < bottomMostPageId; i += 1) {
        channelsCache[i] = channelsCache[i + 1];
        guideRowsCache[i] = guideRowsCache[i + 1];
    }
    channelsCache[bottomMostPageId] = [];
    guideRowsCache[bottomMostPageId] = [];

    dispatch(setGridGuideCacheAction(channelsCache, guideRowsCache));
    // dispatch(fetchAndUpdateChannelsCache(bottomMostPageId));
    dispatch(clearGridGuideCache());
};

export const setGridGuideChannels =
    (isPrevious: boolean = false) =>
    (dispatch: Function, getState: Function) => {
        const { offset, LIMIT, channelsCount } = getState().gridGuide;
        const { verticalPageCount } = getState().guideHighlight;
        const { channelsType } = getState().channelOptionsOverlay;

        let mVerticalPageCount = 0;
        let mVerticalPosition = 0;
        let mCurrentOffset = 0;

        if (isPrevious) {
            mVerticalPageCount = verticalPageCount - 1;
            mVerticalPosition = LIMIT - 1;
            mCurrentOffset = (offset - LIMIT + channelsCount) % channelsCount;
        } else {
            mVerticalPageCount = verticalPageCount + 1;
            mVerticalPosition = 0;
            mCurrentOffset = (offset + LIMIT) % channelsCount;
        }

        dispatch(setVerticalPageCount(mVerticalPageCount));
        dispatch(setGridGuideOffsetAction(mCurrentOffset));
        dispatch(setVerticalPosition(mVerticalPosition));
        dispatch(resetGridGuideChannelsAction());
        dispatch(resetGridGuideRowsAction());
        dispatch(clearGridGuideCache());

        const services: ServicesClientImpl = new ServicesClientImpl();
        const fetchChannels = services.fetchChannels.bind(services);
        RetryMechanism.callServiceWithRetry<TChannels>(
            fetchChannels,
            [channelsType, mCurrentOffset, LIMIT],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TChannels) => {
                const stationIds = <string[]>response.channels.map((channel: Channel) => {
                    return channel.stationId;
                });
                dispatch(
                    loadGridGuideChannelsAction(
                        response.channels,
                        response.channelCount,
                        stationIds,
                        false,
                    ),
                );
            })
            .catch((error) => {
                dispatch(setIsChannelsServiceFailAction(true));
                dispatch(showTemporaryGuideIssueOverlay());
                Diagnostics.error(
                    TAG,
                    'Failed to get channels.fetchAndUpdateChannels -> services.fetchChannels.',
                    error,
                );
            });
    };

export const onChannelsChangeUpdateGridGuide =
    (isPrevious: boolean = false) =>
    (dispatch: Function, getState: Function) => {
        const {
            offset,
            LIMIT,
            channelsCount,
            channelsCache,
            guideRowsCache,
            guideFocusVerticalPageId,
            guideFocusHorizontalPageId,
        } = getState().gridGuide;
        const { verticalPageCount } = getState().guideHighlight;

        let mVerticalPageCount: number = 0;
        let mVerticalPosition: number = 0;
        let mCurrentOffset: number = 0;
        let mCurrentChannelsCacheIndex: number = 0;
        let mChannels: Array<Channel> = [];
        let mPastGuideRows: GridGuideRow[] = [];
        let mPresentGuideRows: GridGuideRow[] = [];
        let mFutureGuideRows: GridGuideRow[] = [];
        let hasValidGuideRows: boolean = false;

        if (isPrevious) {
            mVerticalPageCount = verticalPageCount - 1;
            mVerticalPosition = LIMIT - 1;
            mCurrentOffset = (offset - LIMIT + channelsCount) % channelsCount;
            mCurrentChannelsCacheIndex = guideFocusVerticalPageId - 1;
        } else {
            mVerticalPageCount = verticalPageCount + 1;
            mVerticalPosition = 0;
            mCurrentOffset = (offset + LIMIT) % channelsCount;
            mCurrentChannelsCacheIndex = guideFocusVerticalPageId + 1;
        }

        mChannels = channelsCache[mCurrentChannelsCacheIndex];

        if (guideRowsCache[mCurrentChannelsCacheIndex]) {
            mPastGuideRows =
                guideRowsCache[mCurrentChannelsCacheIndex][guideFocusHorizontalPageId - 1] || [];
            mPresentGuideRows =
                guideRowsCache[mCurrentChannelsCacheIndex][guideFocusHorizontalPageId] || [];
            mFutureGuideRows =
                guideRowsCache[mCurrentChannelsCacheIndex][guideFocusHorizontalPageId + 1] || [];
        } else {
            mPastGuideRows = [];
            mPresentGuideRows = [];
            mFutureGuideRows = [];
        }

        hasValidGuideRows =
            mPastGuideRows.length > 0 &&
            mPresentGuideRows.length > 0 &&
            mFutureGuideRows.length > 0;

        dispatch(setVerticalPageCount(mVerticalPageCount));
        dispatch(setGridGuideOffsetAction(mCurrentOffset));
        dispatch(setVerticalPosition(mVerticalPosition));

        if (mChannels && mChannels.length > 0) {
            const stationIds: string[] = <string[]>mChannels.map((channel) => channel.stationId);
            dispatch(resetGridGuideChannelsAction());
            dispatch(resetGridGuideRowsAction());
            dispatch(loadGridGuideChannelsAction(mChannels, channelsCount, stationIds, false));

            if (hasValidGuideRows) {
                dispatch(
                    setGridGuideRowsAction(mPastGuideRows, mPresentGuideRows, mFutureGuideRows),
                );
            } else {
                dispatch(loadGridGuidePastRows());
                dispatch(loadGridGuideRows());
                dispatch(loadGridGuideFutureRows());
            }
        } else {
            dispatch(loadGridGuideChannels());
        }
    };

export const fetchAndUpdateChannelsCache =
    (pageId: number) => (dispatch: Function, getState: Function) => {
        const { offset, LIMIT, guideFocusVerticalPageId, channelsCount, channelsCache } =
            getState().gridGuide;
        const { channelsType } = getState().channelOptionsOverlay;

        let currentOffset: number = offset;
        if (pageId < guideFocusVerticalPageId) {
            currentOffset = (offset - LIMIT * (pageId + 1) + channelsCount) % channelsCount;
        }

        if (pageId > guideFocusVerticalPageId) {
            currentOffset = (offset + LIMIT * (pageId - 1)) % channelsCount;
        }

        const services: ServicesClientImpl = new ServicesClientImpl();
        const fetchChannels = services.fetchChannels.bind(services);
        RetryMechanism.callServiceWithRetry<TChannels>(
            fetchChannels,
            [channelsType, currentOffset, LIMIT],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TChannels) => {
                channelsCache[pageId] = response.channels;
                dispatch(setGridGuideChannelsCacheAction(channelsCache));
                dispatch(updateGridGuideRowsCache(pageId));
            })
            .catch((error) => {
                Diagnostics.error(
                    TAG,
                    'Failed to fetch channels.fetchAndUpdateChannelsCache() -> services.fetchChannels',
                    error,
                );
            });
    };

export const updateGridGuideRowsCache =
    (toVerticalPageId: number) => (dispatch: Function, getState: Function) => {
        const { guideHorizontalPageLimit, guideFocusHorizontalPageId } = getState().gridGuide;

        // Loading Past, Present & Future Guide data First
        const pastPageId = guideFocusHorizontalPageId - 1;
        const futurePageId = guideFocusHorizontalPageId + 1;
        for (let pageId = pastPageId; pageId <= futurePageId; pageId += 1) {
            dispatch(fetchAndUpdateGuideRowsCache(toVerticalPageId, pageId));
        }

        // Loading rest of the guide data
        for (let pageId = 0; pageId < guideHorizontalPageLimit; pageId += 1) {
            const loadedPages = pageId >= pastPageId && pageId <= futurePageId;
            if (!loadedPages) dispatch(fetchAndUpdateGuideRowsCache(toVerticalPageId, pageId));
        }
    };

export const loadGridGuidePreviousPageRows = () => (dispatch: Function, getState: Function) => {
    const { guideHorizontalPageLimit, guideVerticalPageLimit, guideRowsCache } =
        getState().gridGuide;
    const firstPageIndex = 0;

    // Shift right and fill left most cell of all guideRowsCache for all channelsCache
    for (let verticalPageId = 0; verticalPageId < guideVerticalPageLimit; verticalPageId += 1) {
        for (let i = guideHorizontalPageLimit - 1; i > firstPageIndex; i -= 1) {
            guideRowsCache[verticalPageId][i] = guideRowsCache[verticalPageId][i - 1];
        }
        guideRowsCache[verticalPageId][firstPageIndex] = [];
        dispatch(setGridGuideRowsCacheAction(guideRowsCache));
        dispatch(fetchAndUpdateGuideRowsCache(verticalPageId, firstPageIndex));
    }
};

export const loadGridGuideNextPageRows = () => (dispatch: Function, getState: Function) => {
    const { guideHorizontalPageLimit, guideVerticalPageLimit, guideRowsCache } =
        getState().gridGuide;
    const nextHorizontalPageIndex = guideHorizontalPageLimit - 1;

    // Shift Left and Fill right most cell of all guideRowsCache for all channelsCache
    for (let verticalPageId = 0; verticalPageId < guideVerticalPageLimit; verticalPageId += 1) {
        for (let i = 0; i < nextHorizontalPageIndex; i += 1) {
            guideRowsCache[verticalPageId][i] = guideRowsCache[verticalPageId][i + 1];
        }
        guideRowsCache[verticalPageId][nextHorizontalPageIndex] = [];
        dispatch(setGridGuideRowsCacheAction(guideRowsCache));
        dispatch(fetchAndUpdateGuideRowsCache(verticalPageId, nextHorizontalPageIndex));
    }
};

export const fetchAndUpdateGuideRowsCache =
    (toVerticalPageId: number, toHorizonalPageId: number) =>
    (dispatch: Function, getState: Function) => {
        const {
            channelsCache,
            currentTimestamp,
            startTime: guideStartTime,
            guideFocusHorizontalPageId,
            guideFocusVerticalPageId,
            isGuideRowsServiceFail,
        } = getState().gridGuide;
        const mChannels: Channel[] = channelsCache[toVerticalPageId];
        if (mChannels.length === 0) return;

        let mFromTime: Date = currentTimestamp;
        if (toHorizonalPageId < guideFocusHorizontalPageId) {
            mFromTime = AppUtils.subtractMinutesFromDateTime(
                currentTimestamp,
                IDLE_GRID_GUIDE_DURATION * (guideFocusHorizontalPageId - toHorizonalPageId),
            );
        }
        if (toHorizonalPageId > guideFocusHorizontalPageId) {
            mFromTime = AppUtils.addMinutesToDateTime(
                currentTimestamp,
                IDLE_GRID_GUIDE_DURATION * (toHorizonalPageId - guideFocusHorizontalPageId),
            );
        }
        const mToTime: Date = AppUtils.addMinutesToDateTime(mFromTime, IDLE_GRID_GUIDE_DURATION);
        const services: ServicesClientImpl = new ServicesClientImpl();
        const fetchGuideRowsData = services.fetchGuideRowsData.bind(services);

        RetryMechanism.callServiceWithRetry<GridGuideRow[]>(
            fetchGuideRowsData,
            [mChannels, mFromTime, mToTime, guideStartTime],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((guideRows) => {
                dispatch(updateGuideRowsCache(toVerticalPageId, mFromTime, guideRows));
                if (toVerticalPageId === guideFocusVerticalPageId) {
                    const presentGuideId = guideFocusHorizontalPageId;
                    const pastGuideId = presentGuideId - 1;
                    const futureGuideId = presentGuideId + 1;
                    switch (toHorizonalPageId) {
                        case pastGuideId:
                            dispatch(loadGridGuidePastRowsAction(guideRows));
                            break;
                        case presentGuideId:
                            NFRLoggings.log(NFREvents.START_LOADING_GUIDE_ROWS);
                            dispatch(loadGridGuideRowsAction(guideRows));
                            NFRLoggings.log(NFREvents.FINISH_LOADING_GUIDE_ROWS);
                            NFRLoggings.log(NFREvents.FINISH_LOADING_GUIDE);
                            break;
                        case futureGuideId:
                            dispatch(loadGridGuideFutureRowsAction(guideRows));
                            break;
                        default:
                    }
                }
                if (isGuideRowsServiceFail) dispatch(hideWhisper());
            })
            .catch((error) => {
                Diagnostics.error(
                    TAG,
                    'Failed to fetch guide rows data.fetchAndUpdateGuideRowsCache() -> services.fetchGuideRowsData',
                    error,
                );
                const rowError = error?.response?.data;
                const guideRowError = new GuideRowsError(rowError?.message, rowError?.code);

                dispatch(
                    showWhisper({
                        message: NetworkManager.isNetworkDisconnected()
                            ? NetworkManager.getNetworkDisconnectedMessage()
                            : Locale.getString(LocaleKeys.GUIDE_WHISPER_MESSAGE),
                        iconType: WhisperTypes.INFO,
                    }),
                );
                VuLogger.logAppError(guideRowError);
                OperationsLogger.logUIErrorCode(guideRowError.getCode());
                NFRLoggings.log(NFREvents.FAILURE_LOADING_GUIDE_ROWS, guideRowError);
                dispatch(guideSetErrorAction(guideRowError));
                dispatch(clearGridGuideCache());
                if (toVerticalPageId === guideFocusVerticalPageId) {
                    const presentGuideId = guideFocusHorizontalPageId;
                    const pastGuideId = presentGuideId - 1;
                    const futureGuideId = presentGuideId + 1;
                    switch (toHorizonalPageId) {
                        case pastGuideId:
                            dispatch(loadGridGuidePastRows());
                            break;
                        case presentGuideId:
                            dispatch(loadGridGuideRows());
                            break;
                        case futureGuideId:
                            dispatch(loadGridGuideFutureRows());
                            break;
                        default:
                    }
                }
            });
    };

export const updateGuideRowsCache =
    (toVerticalPageId: number, forTime: Date, guideRows: GridGuideRow[]) =>
    (dispatch: Function, getState: Function) => {
        const {
            currentTimestamp,
            guideFocusHorizontalPageId,
            guideRowsCache,
            guideHorizontalPageLimit,
        } = getState().gridGuide;

        const cacheStartTime = AppUtils.subtractMinutesFromDateTime(
            currentTimestamp,
            guideFocusHorizontalPageId * IDLE_GRID_GUIDE_DURATION,
        );
        const currentIndex: number =
            (forTime.getTime() - cacheStartTime.getTime()) /
            (MILLISECONDS_IN_A_SECOND * IDLE_GRID_GUIDE_DURATION_IN_SECOND);

        const mGuideRowsData: Array<GridGuideRow[][]> = guideRowsCache.map(
            (mGuideRows: GridGuideRow[]) => [...mGuideRows],
        );
        if (currentIndex >= 0 && currentIndex < guideHorizontalPageLimit)
            mGuideRowsData[toVerticalPageId][currentIndex] = guideRows;
        dispatch(setGridGuideRowsCacheAction(mGuideRowsData));
    };

/**
 * This fn will set the startTime of the guideHeader
 * startTime is calculated based on nearest half hour.
 * eg. if time is 10:53am starTime will be 10:30am, if time is 12:11pm startTime will be 12:00pm
 */
export const setGuideStartTime = () => (dispatch: Function) => {
    const dateTime = new Date();
    const minutes = dateTime.getMinutes();
    if (minutes < 30) dateTime.setMinutes(0);
    else dateTime.setMinutes(30);
    dateTime.setSeconds(0);
    dateTime.setMilliseconds(0);
    const currentTimestamp = dateTime;
    const nextTimestamp = AppUtils.addMinutesToDateTime(dateTime, IDLE_GRID_GUIDE_DURATION);
    const previousTimestamp = AppUtils.subtractMinutesFromDateTime(
        dateTime,
        IDLE_GRID_GUIDE_DURATION,
    );
    dispatch(setGuideStartTimeAction(dateTime, previousTimestamp, currentTimestamp, nextTimestamp));
};

export const loadGridGuideChannels = () => (dispatch: Function, getState: Function) => {
    const { channelsType } = getState().channelOptionsOverlay;
    const { gridGuide } = getState();
    const { offset, LIMIT, guideFocusVerticalPageId, channelsCache } = gridGuide;
    const services = new ServicesClientImpl();

    dispatch(resetGridGuideChannelsAction());
    dispatch(resetGridGuideRowsAction());

    const fetchChannels = services.fetchChannels.bind(services);
    RetryMechanism.callServiceWithRetry<TChannels>(
        fetchChannels,
        [channelsType, offset, LIMIT],
        <RetryOptions>DEFAULT_RETRY_OPTIONS,
    )
        .then((response: TChannels) => {
            const stationIds = <string[]>response.channels.map((channel: Channel) => {
                return channel.stationId;
            });
            dispatch(
                loadGridGuideChannelsAction(
                    response.channels,
                    response.channelCount,
                    stationIds,
                    false,
                ),
            );
            channelsCache[guideFocusVerticalPageId] = response.channels;
            dispatch(setGridGuideChannelsCacheAction(channelsCache));
            dispatch(updateGridGuideRowsCache(guideFocusVerticalPageId));
            dispatch(cacheGridGuideChannels());
        })
        .catch((error) => {
            let guideChannelError: any;
            if (error instanceof TypedChannelsNotFoundError) {
                if (channelsType === Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES)) {
                    dispatch(showEmptyGuideScreen());
                    return;
                }
                guideChannelError = error;
            } else {
                const channelError = error?.response?.data;
                guideChannelError = new GuideChannelsError(
                    channelError?.message,
                    channelError?.code,
                );
            }
            NFRLoggings.log(NFREvents.FAILURE_LOADING_CHANNELS, guideChannelError);
            VuLogger.logAppError(guideChannelError);
            OperationsLogger.logUIErrorCode(guideChannelError.getCode());
            dispatch(guideSetErrorAction(guideChannelError));
            Diagnostics.error(
                TAG,
                'Failed to get grid guide Channels.loadGridGuideChannels() -> services.fetchChannels',
                error,
            );
            RecoveryMechanism.callServiceWithRecovery(fetchChannels, [channelsType, offset, LIMIT])
                .then(() => {
                    const { isOverlayDialogActive, uiProperties } = getState().overlayDialog;
                    const isTemporaryGuideIssueOverlayActive: boolean =
                        isOverlayDialogActive &&
                        uiProperties.overlayType === OverlayTypes.TEMPORARY_GUIDE_ISSUE;

                    if (isTemporaryGuideIssueOverlayActive)
                        dispatch(hideTemporaryGuideIssueOverlay());

                    dispatch(refreshGridGuide());
                })
                .catch((recoveryError) => {
                    Diagnostics.error(
                        TAG,
                        'Failed to get grid guide channels with recovery mechanism -> RecoveryMechanism.callServiceWithRecovery',
                        recoveryError,
                    );
                });
            dispatch(setIsChannelsServiceFailAction(true));
            dispatch(showTemporaryGuideIssueOverlay(guideChannelError));
        });
};

export const loadGridGuideRows = () => (dispatch: Function) => {
    dispatch(fetchAndStoreGuideRows(LOAD_GRID_GUIDE_ROWS));
};

export const loadGridGuidePastRows = () => (dispatch: Function) => {
    dispatch(fetchAndStoreGuideRows(LOAD_GRID_GUIDE_PAST_ROWS));
};

export const loadGridGuideFutureRows = () => (dispatch: Function) => {
    dispatch(fetchAndStoreGuideRows(LOAD_GRID_GUIDE_FUTURE_ROWS));
};

export const fetchAndStoreGuideRows =
    (rowsType: string) => (dispatch: Function, getState: Function) => {
        const { channels, currentTimestamp, startTime } = getState().gridGuide;
        const { isWhisperActive } = getState().whisper;
        if (channels.length === 0) return;

        let fromTime: Date = currentTimestamp;
        let toTime: Date = AppUtils.addMinutesToDateTime(fromTime, IDLE_GRID_GUIDE_DURATION);
        if (rowsType === LOAD_GRID_GUIDE_PAST_ROWS) {
            fromTime = AppUtils.subtractMinutesFromDateTime(
                currentTimestamp,
                IDLE_GRID_GUIDE_DURATION,
            );
            toTime = currentTimestamp;
        } else if (rowsType === LOAD_GRID_GUIDE_FUTURE_ROWS) {
            fromTime = AppUtils.addMinutesToDateTime(currentTimestamp, IDLE_GRID_GUIDE_DURATION);
            toTime = AppUtils.addMinutesToDateTime(fromTime, IDLE_GRID_GUIDE_DURATION);
        }

        const services: ServicesClientImpl = new ServicesClientImpl();
        const fetchGuideRowsData = services.fetchGuideRowsData.bind(services);
        RetryMechanism.callServiceWithRetry<GridGuideRow[]>(
            fetchGuideRowsData,
            [channels, fromTime, toTime, startTime],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((guideRows) => {
                switch (rowsType) {
                    case LOAD_GRID_GUIDE_PAST_ROWS:
                        dispatch(loadGridGuidePastRowsAction(guideRows));
                        break;
                    case LOAD_GRID_GUIDE_ROWS:
                        dispatch(loadGridGuideRowsAction(guideRows));
                        if (isWhisperActive) dispatch(hideWhisper());
                        break;
                    case LOAD_GRID_GUIDE_FUTURE_ROWS:
                        dispatch(loadGridGuideFutureRowsAction(guideRows));
                        break;
                    default:
                }
            })
            .catch((modifiedError) => {
                Diagnostics.error(
                    TAG,
                    'Failed to fetch guide rows data.fetchAndStoreGuideRows() -> services.fetchGuideRowsData',
                    modifiedError,
                );
                dispatch(onGuideRowsFailOperations(modifiedError.dummyGuideRows));

                if (modifiedError?.response?.status === 400) {
                    dispatch(clearGridGuideCache());
                } else {
                    RecoveryMechanism.callServiceWithRecovery<GridGuideRow[]>(fetchGuideRowsData, [
                        channels,
                        fromTime,
                        toTime,
                        startTime,
                    ])
                        .then(() => {
                            dispatch(refreshGridGuide());
                        })
                        .catch((error) => {
                            Diagnostics.error(TAG, `Unable to recover guide rows data`, error);
                        });
                }
            });
    };

export const setGridGuideOffset = () => (dispatch: Function, getState: Function) => {
    const { gridGuide } = getState();
    const { LIMIT, channelsCount, offset } = gridGuide;
    const currentOffset = (offset + LIMIT) % channelsCount;
    dispatch(setGridGuideOffsetAction(currentOffset));
    dispatch(loadGridGuideChannels());
};

export const setGridGuideTimestampWindow =
    (prevTimestamp: Date, currentTimestamp: Date, nextTimestamp: Date) => (dispatch: Function) => {
        dispatch(setGridGuideTimestampWindowAction(prevTimestamp, currentTimestamp, nextTimestamp));
    };

export const loadGridGuide = () => (dispatch: Function, getState: Function) => {
    NFRLoggings.log(NFREvents.START_LOADING_GUIDE_ROWS);
    const { channelOptionsOverlay, gridGuide } = getState();
    const { channelsType } = channelOptionsOverlay;
    const { isPastGuideMode } = gridGuide;

    if (isPastGuideMode) {
        dispatch(setIsPastGuideModeAction(false));
    }
    dispatch(setHorizontalPageCount(0));
    dispatch(setGuideStartTime());

    const channel = VideoPlaybackController.getInstance().getPlayable()?.channel;

    const startAtChannelNumber =
        channel?.channelNumber || Storage.getInstance().getItem(Storage.Keys.LAST_TUNED_CHANNEL);

    if (startAtChannelNumber) {
        dispatch(clearGridGuideCache());
        const services = new ServicesClientImpl();
        const fetchNextChannelsFromChannelNumber =
            services.fetchNextChannelsFromChannelNumber.bind(services);
        RetryMechanism.callServiceWithRetry<TChannels>(
            fetchNextChannelsFromChannelNumber,
            [startAtChannelNumber, channelsType, 1],
            <RetryOptions>DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TChannels) => {
                NFRLoggings.log(NFREvents.FINISH_LOADING_GUIDE_ROWS);
                dispatch(setGridGuideOffsetAction(response.currentOffset));
                dispatch(loadGridGuideChannels());
                dispatch(setVerticalPosition(0));
            })
            .catch((error) => {
                dispatch(loadGridGuideChannels());
                Diagnostics.error(
                    TAG,
                    'Failed to fetch next channels from channel number.loadGridGuide() -> services.fetchNextChannelsFromChannelNumber',
                    error,
                );
            });
    } else dispatch(loadGridGuideChannels());
};

export const refreshGridGuideHeader = () => (dispatch: Function) => {
    dispatch(setGuideStartTime());
};

export const turnOnPastGuideMode = () => (dispatch: Function) => {
    dispatch(setIsPastGuideModeAction(true));
};

export const turnOffPastGuideMode = () => (dispatch: Function) => {
    dispatch(setIsPastGuideModeAction(false));
};

export const onLeftSetPreviousPage = () => (dispatch: Function, getState: Function) => {
    NFRLoggings.log(NFREvents.START_LOADING_GUIDE);
    const { guideHighlight, gridGuide } = getState();
    const service = new ServicesClientImpl();
    const {
        pastGuideRows,
        previousTimestamp,
        currentTimestamp,
        guideRows,
        isPastGuideMode,
        guideRowsCache,
        guideFocusVerticalPageId,
        guideFocusHorizontalPageId,
    } = gridGuide;
    const { horizontalPageCount, verticalPosition } = guideHighlight;

    const hasValidPastGuideRows = pastGuideRows && pastGuideRows.length > 0;
    const pastGuideMaxPageLoad =
        service.getBrandingUiInfoForKey(BrandingUiInfoKeys.EPG_LOOK_BACK_HOURS) / 2 ||
        DEFAULT_PAST_GUIDE_MAX_PAGE_LOAD;
    const hasNotValidPastGuidePage =
        isPastGuideMode && Math.abs(horizontalPageCount) > pastGuideMaxPageLoad;

    if (!hasValidPastGuideRows) {
        return;
    }

    if (hasNotValidPastGuidePage) {
        Diagnostics.info(TAG, 'The past guide page limit reached, aborted the key operation.');
        return;
    }

    const mCurrentTimestamp: Date = previousTimestamp;
    const mNextTimestamp: Date = currentTimestamp;
    const mPreviousTimestamp: Date = AppUtils.subtractMinutesFromDateTime(
        mCurrentTimestamp,
        IDLE_GRID_GUIDE_DURATION,
    );
    const { guideCells } = pastGuideRows[verticalPosition];
    dispatch(setHorizontalPageCountAction(horizontalPageCount - 1));
    dispatch(resetGridGuideRowsAction());
    dispatch(
        onLeftSetPreviousPageAction(
            mPreviousTimestamp,
            mCurrentTimestamp,
            mNextTimestamp,
            guideRows,
            pastGuideRows,
        ),
    );

    const mPastGuideRows: GridGuideRow[] =
        guideRowsCache[guideFocusVerticalPageId][guideFocusHorizontalPageId - 2] || [];
    if (mPastGuideRows.length > 0) {
        dispatch(loadGridGuidePastRowsAction(mPastGuideRows));
    } else {
        dispatch(loadGridGuidePastRows());
    }
    dispatch(loadGridGuidePreviousPageRows());

    if (guideCells && guideCells.length > 0) {
        const horizontalPosition = guideCells.length;

        dispatch(setHorizontalPosition(horizontalPosition));
    }
};

export const onRightSetNextPage = () => (dispatch: Function, getState: Function) => {
    NFRLoggings.log(NFREvents.START_LOADING_GUIDE);
    const { guideHighlight, gridGuide } = getState();
    const { horizontalPageCount } = guideHighlight;
    const {
        guideRows,
        currentTimestamp,
        nextTimestamp,
        futureGuideRows,
        guideRowsCache,
        guideFocusVerticalPageId,
        guideFocusHorizontalPageId,
    } = gridGuide;

    if (futureGuideRows.length === 0) return;
    if (horizontalPageCount < FUTURE_GUIDE_MAX_PAGE_LOAD) {
        const mPreviousTimestamp: Date = currentTimestamp;
        const mCurrentTimestamp: Date = nextTimestamp;
        const mNextTimestamp: Date = AppUtils.addMinutesToDateTime(
            mCurrentTimestamp,
            IDLE_GRID_GUIDE_DURATION,
        );
        dispatch(setHorizontalPageCountAction(horizontalPageCount + 1));
        dispatch(resetGridGuideRowsAction());
        dispatch(
            onRightSetNextPageAction(
                mPreviousTimestamp,
                mCurrentTimestamp,
                mNextTimestamp,
                guideRows,
                futureGuideRows,
            ),
        );

        const mFutureGuideRows: GridGuideRow[] =
            guideRowsCache[guideFocusVerticalPageId][guideFocusHorizontalPageId + 2] || [];
        if (mFutureGuideRows.length > 0) {
            dispatch(loadGridGuideFutureRowsAction(mFutureGuideRows));
        } else {
            dispatch(loadGridGuideFutureRows());
        }
        dispatch(loadGridGuideNextPageRows());
        dispatch(setHorizontalPosition(1));
    } else {
        Diagnostics.info(TAG, 'The future guide page limit reached, aborted the key operation.');
    }
};

export const onRightGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { guideHighlight, gridGuide } = getState();
    const { horizontalPosition, verticalPosition, horizontalPageCount } = guideHighlight;
    const { guideRows, nextTimestamp, isPastGuideMode } = gridGuide;

    if (guideRows && guideRows.length !== 0 && guideRows[verticalPosition]) {
        const { guideCells } = guideRows[verticalPosition];

        const isStartOfGuideCells =
            isPastGuideMode &&
            Math.abs(horizontalPageCount) === 1 &&
            horizontalPosition === guideCells.length;

        const isLastGuideCell =
            horizontalPosition && horizontalPosition > 0 && horizontalPosition < guideCells.length;

        if (isStartOfGuideCells) {
            dispatch(setHorizontalPosition(0));
        } else if (guideCells && !guideCells[horizontalPosition]) {
            dispatch(onRightSetNextPage());
        } else if (isLastGuideCell) {
            const { startTime, duration } = guideCells[horizontalPosition - 1];
            const sessionTime = AppUtils.addSecondsToDateTime(startTime, duration);

            if (sessionTime.getTime() === new Date(nextTimestamp).getTime()) {
                dispatch(onRightSetNextPage());
            } else {
                dispatch(setHorizontalPosition(horizontalPosition + 1));
            }
        } else {
            dispatch(setHorizontalPosition(horizontalPosition + 1));
        }
    }
};

export const onGuideRowsFailOperations =
    (dummyGuideRows) => (dispatch: Function, getState: Function) => {
        const { isGuideRowsServiceFail, startTime } = getState().gridGuide;
        const { horizontalPosition } = getState().guideHighlight;
        dispatch(
            setIsGuideRowsServiceFailAction(
                true,
                dummyGuideRows,
                AppUtils.subtractMinutesFromDateTime(startTime, IDLE_GRID_GUIDE_DURATION),
                startTime,
                AppUtils.addMinutesToDateTime(startTime, IDLE_GRID_GUIDE_DURATION),
            ),
        );
        if (!isGuideRowsServiceFail) {
            dispatch(
                showWhisper({
                    message: NetworkManager.isNetworkDisconnected()
                        ? NetworkManager.getNetworkDisconnectedMessage()
                        : Locale.getString(LocaleKeys.GUIDE_WHISPER_MESSAGE),
                    iconType: WhisperTypes.INFO,
                }),
            );
            if (horizontalPosition > 0) dispatch(setGuideHighlightToInitialPosition(startTime));
            dispatch(turnOffPastGuideMode());
        }
    };

export const refreshGridGuide = () => (dispatch: Function) => {
    const currentTime = AppUtils.roundTimeToNearestInterval(new Date(), 30, true);
    const mStartTime = currentTime;
    const mCurrentTimestamp = currentTime;
    const mNextTimestamp = AppUtils.addMinutesToDateTime(currentTime, IDLE_GRID_GUIDE_DURATION);
    const mPreviousTimestamp = AppUtils.subtractMinutesFromDateTime(
        currentTime,
        IDLE_GRID_GUIDE_DURATION,
    );
    dispatch(
        refreshGridGuideAction(mStartTime, mCurrentTimestamp, mNextTimestamp, mPreviousTimestamp),
    );
    dispatch(setHorizontalPageCount(0));
    dispatch(setHorizontalPosition(1));
    dispatch(loadGridGuideChannels());
};

export const clearGridGuideCache = () => (dispatch: Function, getState: Function) => {
    const { guideHorizontalPageLimit, guideVerticalPageLimit } = getState().gridGuide;
    const emptyChannelsCache = new Array(guideVerticalPageLimit).fill([]);
    const emptyGuideRowsCache = new Array(guideVerticalPageLimit).fill(
        new Array(guideHorizontalPageLimit).fill([]),
    );
    dispatch(setGridGuideCacheAction(emptyChannelsCache, emptyGuideRowsCache));
};

export const guideSetCaretVisible = () => (dispatch: Function) => {
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.GUIDE_INTERACTIVE_LAYER_TASKS));
    dispatch(guideSetIsCaretVisibleAction(true));
};

export const guideSetCaretInvisible = () => (dispatch: Function, getState: Function) => {
    const { previousScreenTaskController, screenTaskController } = getState().screens;
    const { isOverlayDialogActive } = getState().overlayDialog;

    if (isOverlayDialogActive) return;

    dispatch(clearActiveInteractiveComponent());
    if (
        screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS &&
        screenTaskController !== ScreenTaskControllerTypes.DEFAULT_SIDEBAR_NAVIGATION_MENU_TASKS
    )
        dispatch(setScreenTaskController(previousScreenTaskController));
    dispatch(guideSetIsCaretVisibleAction(false));
};

export const guideHideSpinner = () => (dispatch: Function, getState: Function) => {
    const { previousScreenTaskController } = getState().screens;
    const { isSpinnerActive } = getState().gridGuide;
    if (!isSpinnerActive) return;
    dispatch(setScreenTaskController(previousScreenTaskController));
    dispatch(guideSetIsSpinnerActiveAction(false));
    dispatch(hideSpinner());
};

export const guideShowSpinner = () => (dispatch: Function, getState: Function) => {
    const { isSpinnerActive } = getState().gridGuide;
    if (isSpinnerActive) return;
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.GUIDE_SPINNER_TASKS));
    dispatch(guideSetIsSpinnerActiveAction(true));
    dispatch(showSpinner('', SpinnerDelays.GUIDE));
};

export default gridGuideReducer;
